import React, { useState, useEffect } from 'react';
import { useAuth, API } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Brain, AlertTriangle, FileText, Wrench, Megaphone, Plus, TrendingUp, Lightbulb, CheckSquare, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import SendDigestButton from '../components/SendDigestButton';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { trackModuleVisit } from '@/lib/posthog';

const DashboardPage = () => {
  const { user, selectedBuilding } = useAuth();
  const navigate = useNavigate();
  const [aiSummary, setAiSummary] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [stats, setStats] = useState({
    pqrs: 0,
    announcements: 0,
    maintenance: 0,
    emergencies: 0
  });

  // Track module visit on mount
  useEffect(() => {
    trackModuleVisit('dashboard');
  }, []);

  // Helper to render bold text from markdown-style **text**
  const renderRichText = (text) => {
    if (!text) return null;
    const parts = text.split(/(\*\*.*?\*\*)/g);
    return parts.map((part, index) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={index} className="font-semibold text-primary">{part.slice(2, -2)}</strong>;
      }
      return part;
    });
  };

  useEffect(() => {
    fetchStats();
    if (user?.role === 'admin') {
      fetchAiSummary();
    }
  }, [user, selectedBuilding]);

  const fetchStats = async () => {
    setLoadingStats(true);
    try {
      const query = selectedBuilding && selectedBuilding !== 'all' ? `?building_id=${selectedBuilding}` : '';
      
      const [pqrsRes, announcementsRes, maintenanceRes, emergenciesRes] = await Promise.all([
        axios.get(`${API}/pqrs${query}`),
        axios.get(`${API}/announcements${query}`),
        axios.get(`${API}/maintenance${query}`),
        axios.get(`${API}/emergencies${query}`)
      ]);

      setStats({
        pqrs: pqrsRes.data.length,
        announcements: announcementsRes.data.length,
        maintenance: maintenanceRes.data.length,
        emergencies: emergenciesRes.data.filter(e => e.status === 'active').length
      });
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    } finally {
      setLoadingStats(false);
    }
  };

  const fetchAiSummary = async () => {
    setLoadingSummary(true);
    try {
      const payload = selectedBuilding && selectedBuilding !== 'all' ? { building_id: selectedBuilding } : {};
      const summaryRes = await axios.post(`${API}/ai/summary`, payload);
      setAiSummary(summaryRes.data);
    } catch (error) {
      console.error('Error fetching AI summary:', error);
      setAiSummary(null);
    } finally {
      setLoadingSummary(false);
    }
  };

  if (loadingStats) {
    return (
      <DashboardLayout>
        <div className="space-y-8">
           <div className="h-8 w-48 bg-slate-200 rounded animate-pulse" />
           <div className="h-64 w-full bg-slate-100 rounded-xl animate-pulse" />
           <div className="grid grid-cols-4 gap-6">
              {[1,2,3,4].map(i => <div key={i} className="h-32 bg-slate-100 rounded-xl animate-pulse" />)}
           </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-10">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex flex-col gap-2">
            <h1 className="text-4xl font-heading font-bold text-slate-900 tracking-tight" data-testid="dashboard-title">
              Hola, {user?.name.split(' ')[0]}
            </h1>
            <p className="text-slate-500 text-lg font-medium">
              {user?.role === 'admin' ? 'Tu centro de comando operativo.' : 'Bienvenido a casa.'}
            </p>
          </div>
          {user?.role === 'admin' && <SendDigestButton />}
        </div>

        {/* AI Summary - Premium Redesign */}
        {user?.role === 'admin' && (
          <div data-testid="ai-summary-card">
             {loadingSummary ? (
               <div className="bg-white rounded-2xl p-8 border border-slate-100 shadow-card">
                 {/* Skeleton Loader - 3 cards animadas */}
                 <div className="flex items-start gap-6">
                   <div className="hidden sm:block h-14 w-14 rounded-2xl bg-[#F3F4F6] animate-pulse" />
                   <div className="flex-1 space-y-6">
                     {/* Header skeleton */}
                     <div className="space-y-3">
                       <div className="h-6 w-48 bg-[#F3F4F6] rounded-lg animate-pulse" />
                       <div className="h-4 w-full bg-[#F3F4F6] rounded-md animate-pulse" />
                       <div className="h-4 w-3/4 bg-[#F3F4F6] rounded-md animate-pulse" />
                     </div>
                     {/* Two column skeleton */}
                     <div className="grid lg:grid-cols-2 gap-8 border-t border-slate-100 pt-6">
                       {/* Left column - Actions */}
                       <div className="space-y-3">
                         <div className="h-4 w-32 bg-[#F3F4F6] rounded animate-pulse" />
                         <div className="h-16 w-full bg-[#F3F4F6] rounded-xl animate-pulse" />
                         <div className="h-16 w-full bg-[#F3F4F6] rounded-xl animate-pulse" />
                       </div>
                       {/* Right column - Predictions */}
                       <div className="space-y-3">
                         <div className="h-4 w-40 bg-[#F3F4F6] rounded animate-pulse" />
                         <div className="h-12 w-full bg-[#F3F4F6] rounded-lg animate-pulse" />
                         <div className="h-12 w-full bg-[#F3F4F6] rounded-lg animate-pulse" />
                       </div>
                     </div>
                   </div>
                 </div>
               </div>
             ) : aiSummary ? (
               <div className="relative overflow-hidden bg-white rounded-2xl border border-indigo-50 shadow-card hover:shadow-premium transition-all duration-500 group">
                 {/* Premium decorative header */}
                 <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-indigo-500 via-purple-500 to-indigo-500"></div>
                 
                 <div className="p-8">
                    <div className="flex items-start gap-6">
                        <div className="hidden sm:flex h-14 w-14 rounded-2xl bg-indigo-50 items-center justify-center text-indigo-600 shadow-sm group-hover:scale-110 transition-transform duration-300">
                            <Sparkles className="h-7 w-7" />
                        </div>
                        <div className="flex-1 space-y-8">
                            {/* Summary Header */}
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                                        Copiloto Operativo
                                        <Badge variant="secondary" className="bg-indigo-50 text-indigo-700 border-indigo-100 font-semibold px-2 py-0.5 text-xs tracking-wide">BETA</Badge>
                                    </h2>
                                    <span className="text-xs font-medium text-slate-400 bg-slate-50 px-3 py-1 rounded-full border border-slate-100">Actualizado hace un momento</span>
                                </div>
                                <div className="prose prose-slate prose-p:text-slate-600 prose-strong:text-slate-900 prose-strong:font-bold max-w-none">
                                    <p className="text-lg leading-relaxed">{renderRichText(aiSummary.summary)}</p>
                                </div>
                            </div>

                            {/* Two Column Layout for Details */}
                            <div className="grid lg:grid-cols-2 gap-8 border-t border-slate-100 pt-8">
                                {/* Left: Action Plan */}
                                <div className="space-y-4">
                                    <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2">
                                        <CheckSquare className="h-4 w-4" /> Acciones Prioritarias
                                    </h3>
                                    <div className="space-y-3">
                                        {(aiSummary.actions && aiSummary.actions.length > 0 ? aiSummary.actions : aiSummary.critical_items || []).map((item, idx) => {
                                            const text = item.text || item;
                                            const priority = item.priority || 'medium';
                                            return (
                                                <div key={idx} className="flex items-start gap-3 p-4 rounded-xl bg-slate-50 border border-slate-100 hover:bg-white hover:border-indigo-100 hover:shadow-subtle transition-all duration-200">
                                                    <div className={`mt-1.5 h-2 w-2 rounded-full flex-shrink-0 ${priority === 'high' ? 'bg-rose-500 shadow-[0_0_8px_rgba(244,63,94,0.4)]' : 'bg-amber-400'}`} />
                                                    <span className="text-sm font-medium text-slate-700 leading-snug">{text}</span>
                                                </div>
                                            );
                                        })}
                                        {(!aiSummary.actions?.length && !aiSummary.critical_items?.length) && (
                                            <p className="text-sm text-slate-400 italic pl-2">Todo en orden. No hay acciones pendientes.</p>
                                        )}
                                    </div>
                                </div>

                                {/* Right: Predictions & Insights */}
                                <div className="space-y-4">
                                    <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2">
                                        <TrendingUp className="h-4 w-4" /> Inteligencia Predictiva
                                    </h3>
                                    {aiSummary.predictions && aiSummary.predictions.length > 0 ? (
                                        <ul className="space-y-3">
                                            {aiSummary.predictions.map((pred, i) => (
                                                <li key={i} className="flex gap-3 text-sm text-slate-600 bg-white p-3 rounded-lg border border-transparent">
                                                    <div className="h-1.5 w-1.5 rounded-full bg-indigo-400 mt-2 flex-shrink-0 opacity-50" />
                                                    <span>{pred}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <p className="text-sm text-slate-400 italic pl-2">Recolectando datos para predicciones...</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                 </div>
               </div>
             ) : (
                <div className="text-center py-12 bg-slate-50 rounded-2xl border border-dashed border-slate-200">
                  <Brain className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500 font-medium">Sistema de IA en espera</p>
                </div>
             )}
          </div>
        )}

        {/* Minimal Metrics Grid */}
        <div>
            <h3 className="text-lg font-bold text-slate-900 mb-6 flex items-center gap-2">
                Métricas Clave <span className="text-xs font-normal text-slate-400 bg-white px-2 py-1 rounded-md border border-slate-100">Hoy</span>
            </h3>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {[
                { label: 'PQRS Activas', value: stats.pqrs, icon: FileText, color: 'text-blue-600', bg: 'bg-blue-50', link: '/pqrs' },
                { label: 'Avisos', value: stats.announcements, icon: Megaphone, color: 'text-purple-600', bg: 'bg-purple-50', link: '/announcements' },
                { label: 'Mantenimiento', value: stats.maintenance, icon: Wrench, color: 'text-amber-600', bg: 'bg-amber-50', link: '/maintenance' },
                { label: 'Emergencias', value: stats.emergencies, icon: AlertTriangle, color: 'text-rose-600', bg: 'bg-rose-50', link: '/emergencies', alert: stats.emergencies > 0 }
            ].map((stat, idx) => (
                <Card 
                    key={idx} 
                    className={`
                        border-0 ring-1 ring-slate-100 shadow-subtle hover:shadow-card-hover hover:ring-slate-200 transition-all duration-300 cursor-pointer group
                        ${stat.alert ? 'ring-rose-100 bg-rose-50/10' : 'bg-white'}
                    `}
                    onClick={() => navigate(stat.link)}
                >
                    <div className="p-6">
                        <div className="flex items-center justify-between mb-4">
                            <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${stat.bg} ${stat.color} group-hover:scale-110 transition-transform duration-300`}>
                                <stat.icon className="h-5 w-5" />
                            </div>
                            {stat.alert && <span className="flex h-2.5 w-2.5 rounded-full bg-rose-500 animate-pulse" />}
                        </div>
                        <div>
                            <span className="text-3xl font-heading font-bold text-slate-900 tracking-tight">{stat.value}</span>
                            <p className="text-sm font-medium text-slate-500 mt-1">{stat.label}</p>
                        </div>
                    </div>
                </Card>
            ))}
            </div>
        </div>

        {/* Quick Actions - Floating Bar Style or Minimal Cards? Let's go minimal cards */}
        {user?.role === 'admin' && (
          <div className="pt-4">
            <h3 className="text-lg font-bold text-slate-900 mb-6">Accesos Directos</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                  { label: 'Crear PQRS', icon: FileText, action: () => navigate('/pqrs') },
                  { label: 'Nuevo Aviso', icon: Megaphone, action: () => navigate('/announcements') },
                  { label: 'Programar Tarea', icon: Wrench, action: () => navigate('/maintenance') },
              ].map((action, idx) => (
                  <button
                    key={idx}
                    onClick={action.action}
                    className="flex items-center gap-4 p-4 rounded-xl bg-white border border-slate-100 shadow-subtle hover:shadow-md hover:border-indigo-100 hover:translate-y-[-2px] transition-all duration-200 group text-left"
                  >
                    <div className="h-10 w-10 rounded-full bg-slate-50 flex items-center justify-center text-slate-600 group-hover:bg-primary group-hover:text-white transition-colors">
                        <action.icon className="h-5 w-5" />
                    </div>
                    <span className="font-semibold text-slate-700 group-hover:text-primary">{action.label}</span>
                  </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default DashboardPage;
